import { useCallback, useRef, type MutableRefObject } from 'react';
import mapboxgl from 'mapbox-gl';
import { buildCircleFeature } from '../map-geometry';
import type { MarkerPreceptor } from './types';

const RADIUS_COLOR = '#2563eb';

export const useRadiusLayers = (mapRef: MutableRefObject<mapboxgl.Map | null>) => {
  const radiusSourceIdRef = useRef(`preceptor-radius-source-${Math.random().toString(36).slice(2)}`);
  const radiusOuterFillLayerIdRef = useRef(`${radiusSourceIdRef.current}-outer-fill`);
  const radiusOutlineLayerIdRef = useRef(`${radiusSourceIdRef.current}-outline`);
  const clickHandlerRef = useRef<((e: mapboxgl.MapMouseEvent) => void) | null>(null);

  const removeRadiusLayers = useCallback(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;
    
    // Remove click handler
    if (clickHandlerRef.current) {
      map.off('click', radiusOuterFillLayerIdRef.current, clickHandlerRef.current);
      clickHandlerRef.current = null;
    }
    
    const layers = [radiusOuterFillLayerIdRef.current, radiusOutlineLayerIdRef.current];
    layers.forEach(layerId => {
      if (map.getLayer(layerId)) {
        map.removeLayer(layerId);
      }
    });
    const sourceId = radiusSourceIdRef.current;
    if (map.getSource(sourceId)) {
      map.removeSource(sourceId);
    }
  }, [mapRef]);

  const renderRadiusLayers = useCallback((options: {
    preceptors: MarkerPreceptor[];
    radiusMiles: number;
    selectedId?: string;
    onMarkerClick?: (id: string) => void;
  }) => {
    const map = mapRef.current;
    if (!map) return;
    const { preceptors, radiusMiles, selectedId, onMarkerClick } = options;
    if (!preceptors.length) {
      removeRadiusLayers();
      return;
    }

    const sourceId = radiusSourceIdRef.current;
    const outerFillLayerId = radiusOuterFillLayerIdRef.current;
    const outlineLayerId = radiusOutlineLayerIdRef.current;

    const featureCollection = {
      type: 'FeatureCollection' as const,
      features: preceptors.map(p => buildCircleFeature({
        id: `${p.id}-outer`,
        latitude: p.latitude!,
        longitude: p.longitude!,
        radiusMiles,
        properties: {
          preceptorId: p.id,
          ring: 'outer',
        },
      })),
    };

    if (map.getSource(sourceId)) {
      (map.getSource(sourceId) as mapboxgl.GeoJSONSource).setData(featureCollection);
    } else {
      map.addSource(sourceId, { type: 'geojson', data: featureCollection });
    }

    if (!map.getLayer(outerFillLayerId)) {
      map.addLayer({
        id: outerFillLayerId,
        type: 'fill',
        source: sourceId,
        filter: ['==', ['get', 'ring'], 'outer'],
        paint: {
          'fill-color': RADIUS_COLOR,
          'fill-opacity': 0.18,
          'fill-opacity-transition': { duration: 200 },
        },
      });
    }

    if (!map.getLayer(outlineLayerId)) {
      map.addLayer({
        id: outlineLayerId,
        type: 'line',
        source: sourceId,
        filter: ['==', ['get', 'ring'], 'outer'],
        paint: {
          'line-color': RADIUS_COLOR,
          'line-width': 1.5,
          'line-dasharray': [2, 2],
          'line-opacity-transition': { duration: 200 },
        },
      });
    }

    if (map.getLayer(outerFillLayerId)) {
      map.setPaintProperty(outerFillLayerId, 'fill-color', RADIUS_COLOR);
      map.setPaintProperty(outerFillLayerId, 'fill-opacity', ['case', ['==', ['get', 'preceptorId'], selectedId ?? ''], 0.32, 0.18]);
    }
    if (map.getLayer(outlineLayerId)) {
      map.setPaintProperty(outlineLayerId, 'line-color', RADIUS_COLOR);
      map.setPaintProperty(outlineLayerId, 'line-width', ['case', ['==', ['get', 'preceptorId'], selectedId ?? ''], 2.8, 1.5]);
      map.setPaintProperty(outlineLayerId, 'line-dasharray', [2, 2]);
    }
    
    // Add click handler for radius circles
    if (onMarkerClick && !clickHandlerRef.current) {
      clickHandlerRef.current = (e: mapboxgl.MapMouseEvent) => {
        const features = map.queryRenderedFeatures(e.point, { layers: [outerFillLayerId] });
        if (features.length > 0) {
          const preceptorId = features[0].properties?.preceptorId;
          if (preceptorId) {
            onMarkerClick(preceptorId);
          }
        }
      };
      map.on('click', outerFillLayerId, clickHandlerRef.current);
      
      // Change cursor on hover
      map.on('mouseenter', outerFillLayerId, () => {
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', outerFillLayerId, () => {
        map.getCanvas().style.cursor = '';
      });
    }
  }, [mapRef, removeRadiusLayers]);

  return { removeRadiusLayers, renderRadiusLayers };
};
